import { createHash } from 'crypto';

/**
 * Generates a cryptographically secure nonce for CSP
 */
export function generateNonce(): string {
  const timestamp = Date.now().toString();
  const random = Math.random().toString();
  return createHash('sha256').update(timestamp + random).digest('base64');
}

// This will be used to store the current nonce
let currentNonce: string | null = null;

/**
 * Gets or creates a nonce for the current session
 */
export function getCurrentNonce(): string {
  if (!currentNonce) {
    currentNonce = generateNonce();
  }
  return currentNonce;
}

/**
 * Generates a new nonce, replacing the current one
 */
export function refreshNonce(): string {
  currentNonce = generateNonce();
  return currentNonce;
}

/**
 * Gets the CSP nonce value from the window object
 * This is injected into the HTML during build time by the vite-plugin-csp
 */
export function getNonce(): string {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined' && (window as any).__nonce) {
    return (window as any).__nonce;
  }
  
  // If no nonce is found in the window object, use the current nonce
  if (typeof window !== 'undefined' && currentNonce) {
    return currentNonce;
  }
  
  // If we're in a server environment or no nonce is available, return empty string
  return '';
}

/**
 * Creates nonced script attributes for use with libraries that require 
 * dynamically adding scripts, such as Web3 libraries
 * @returns An object containing the nonce attribute if available
 */
export function getNonceProps(): Record<string, string> {
  const nonce = getNonce();
  return nonce ? { nonce } : {};
}

/**
 * Verifies if a given script element has a valid CSP nonce
 * @param scriptEl The script element to check
 * @returns true if the script has a valid nonce, false otherwise
 */
export function hasValidNonce(scriptEl: HTMLScriptElement): boolean {
  const nonce = getNonce();
  
  if (!nonce) {
    return true; // If there's no nonce system in place, consider it valid
  }
  
  const scriptNonce = scriptEl.getAttribute('nonce');
  return scriptNonce === nonce;
}

/**
 * Adds nonce to an existing HTMLElement
 * @param el The element to add nonce to
 */
export function addNonceToElement(el: HTMLElement): void {
  const nonce = getNonce();
  if (nonce && !el.hasAttribute('nonce')) {
    el.setAttribute('nonce', nonce);
  }
}
